import thaiBanksLogo from 'thai-banks-logo/dist/index'

export const thaiYear = (date: Date) => date.getFullYear() + 543

export const THAI_MONTHS = [
  'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
  'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
]

export const formatAmount = (n: number | null | undefined): string => {
  if (n == null) return '—'
  return '฿' + Number(n).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const BANGKOK_TIME_ZONE = 'Asia/Bangkok'

function parseNumericDatetime(input: number): Date | null {
  const ms = input > 1e12 ? input : input * 1000
  const d = new Date(ms)
  return Number.isNaN(d.getTime()) ? null : d
}

export function parseDatetimeInput(input: string | number): Date | null {
  if (typeof input === 'number' && Number.isFinite(input)) {
    return parseNumericDatetime(input)
  }

  const s = String(input).trim()
  if (!s) return null

  if (/^\d{10,13}$/.test(s)) {
    return parseNumericDatetime(Number(s))
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const d = new Date(`${s}T00:00:00+07:00`)
    return Number.isNaN(d.getTime()) ? null : d
  }

  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? null : d
}

function getThaiBangkokDateParts(date: Date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: BANGKOK_TIME_ZONE,
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date)

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? ''

  const day = get('day')
  const month = THAI_MONTHS[Number(get('month')) - 1] ?? get('month')
  const beYear = Number(get('year')) + 543
  const hour = get('hour').padStart(2, '0')
  const minute = get('minute')

  return { day, month, beYear, hour, minute }
}

/** Card/list date: "22 ก.ค. 2569" */
export function formatThaiDateCardFromDate(date: Date): string {
  const { day, month, beYear } = getThaiBangkokDateParts(date)
  return `${day} ${month} ${beYear}`
}

export function formatThaiDateCard(
  iso: string | number | null | undefined,
): string {
  if (iso == null || iso === '') return '—'
  try {
    const d = parseDatetimeInput(iso)
    if (!d) return '—'
    return formatThaiDateCardFromDate(d)
  } catch {
    return '—'
  }
}

export function formatThaiDateFromDate(date: Date): string {
  return formatThaiDateCardFromDate(date)
}

export function formatThaiDatetimePartsFromDate(date: Date): {
  dateLabel: string
  timeLabel: string
} {
  const { day, month, beYear, hour, minute } = getThaiBangkokDateParts(date)
  return {
    dateLabel: `${day} ${month} ${beYear}`,
    timeLabel: `${hour}:${minute}`,
  }
}

export function formatThaiDatetimeFromDate(date: Date): string {
  const { dateLabel, timeLabel } = formatThaiDatetimePartsFromDate(date)
  return `${dateLabel} (${timeLabel})`
}

export const formatThaiDate = (
  iso: string | number | null | undefined,
): string => {
  if (iso == null || iso === '') return '—'
  try {
    const d = parseDatetimeInput(iso)
    if (!d) return '—'
    return formatThaiDateFromDate(d)
  } catch {
    return '—'
  }
}

export const formatThaiDatetime = (
  iso: string | number | null | undefined,
): string => {
  if (iso == null || iso === '') return '—'
  try {
    const d = parseDatetimeInput(iso)
    if (!d) return '—'
    return formatThaiDatetimeFromDate(d)
  } catch {
    return '—'
  }
}

export interface ThaiBankLogoEntry {
  icon?: string
  symbol?: string
  name?: string
  nameLong?: string
  nameEN?: string
}

export const THAI_BANKS: Record<string, ThaiBankLogoEntry> = (() => {
  const raw = (thaiBanksLogo as { default?: unknown } | undefined)?.default ?? thaiBanksLogo
  return ((raw as { default?: unknown } | undefined)?.default ?? raw ?? {}) as Record<string, ThaiBankLogoEntry>
})()

export const THAI_BANK_ALIASES: Record<string, string> = {
  // Back-compat keys used by older data.
  KBank: 'KBANK',
  LHBANK: 'LHB',
  TMB: 'TTB',
}

export function normalizeThaiBankKey(input: unknown): string | null {
  const raw = String(input ?? '').trim()
  if (!raw) return null

  const upper = raw.toUpperCase()
  if (THAI_BANKS[raw]) return raw
  if (THAI_BANKS[upper]) return upper
  if (THAI_BANK_ALIASES[raw]) return THAI_BANK_ALIASES[raw]
  if (THAI_BANK_ALIASES[upper]) return THAI_BANK_ALIASES[upper]

  for (const [key, bank] of Object.entries(THAI_BANKS)) {
    if (!bank) continue
    if (bank.symbol && String(bank.symbol).toUpperCase() === upper) return key
    if (bank.nameLong && String(bank.nameLong).trim() === raw) return key
    if (bank.name && String(bank.name).trim() === raw) return key
    if (bank.nameEN && String(bank.nameEN).trim().toUpperCase() === upper) return key
  }

  return null
}

export const BANK_LOGOS: Record<string, string> = (() => {
  const out: Record<string, string> = {}

  for (const [key, bank] of Object.entries(THAI_BANKS)) {
    if (bank?.icon) out[key] = bank.icon
    if (bank?.symbol && bank?.icon) out[bank.symbol] = bank.icon
  }

  for (const [alias, key] of Object.entries(THAI_BANK_ALIASES)) {
    if (out[key]) out[alias] = out[key]
  }

  return out
})()

/** Canonical expense document_type stored on the record. */
export function normalizeExpenseDocumentType(
  raw: unknown,
): "receipt" | "invoice" | "" {
  const text = String(raw ?? "").trim()
  if (!text) return ""
  if (/invoice|ใบแจ้งหนี้|ใบกำกับ/i.test(text)) return "invoice"
  if (/receipt|ใบเสร็จ/i.test(text)) return "receipt"
  return ""
}

/** Table/drawer label: receipt → ใบเสร็จ, invoice → ใบแจ้งหนี้. */
export function formatExpenseDocumentType(raw: unknown): string {
  const code = normalizeExpenseDocumentType(raw)
  if (code === "invoice") return "ใบแจ้งหนี้"
  if (code === "receipt") return "ใบเสร็จ"
  const text = String(raw ?? "").trim()
  return text
}
